import React, { useContext, useState } from 'react';
import { AuthContext } from '../../context/AuthContext';
// Import useLocation to access the state passed from the ProtectedRoute
import { useNavigate, useLocation } from 'react-router-dom';
import API_URL from '../common/api'; // your API base URL
import { VET_ROLE_ID } from '../../context/PermissionConstants';

function LoginPage() {
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();
    // Use useLocation to get the current location object
    const location = useLocation();

    const [username, setUsername] = useState(location.state?.username || '');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const successMessage = location.state?.message || '';

    // First-time (temporary password) flow — toggles a small email-only view.
    const [mode, setMode] = useState('login'); // 'login' | 'first' | 'forgot'
    const [firstEmail, setFirstEmail] = useState('');
    const [firstError, setFirstError] = useState('');
    const [firstMessage, setFirstMessage] = useState('');
    const [firstLoading, setFirstLoading] = useState(false);

    // Forgot-password flow — for people who already have an account.
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotError, setForgotError] = useState('');
    const [forgotMessage, setForgotMessage] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/auth/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
                credentials: 'include',
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.msg || 'Login failed');
            }

            const data = await response.json();

            // This will store user + token in AuthContext
            login(data.token, data.user);

            const isVet = Number(data.user.role_id) === VET_ROLE_ID;
            if (data.user.must_change_password) {
                setTimeout(() => {
                    navigate('/myaccount?force=true', { replace: true });
                }, 10);
            } else {
                setTimeout(() => {
                    // Veterinarians work from the existing clinical workflow,
                    // not the staff dashboard.
                    const fromPath = isVet ? '/workflow' : (location.state?.from?.pathname || '/');
                    navigate(fromPath, { replace: true });
                }, 10);
            }
        } catch (err) {
            setError(
                err.message.includes('Invalid credentials')
                    ? 'Incorrect email or password'
                    : err.message || 'Login failed'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleFirstTime = async (e) => {
        e.preventDefault();
        setFirstError('');
        setFirstMessage('');
        setFirstLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/auth/first-time`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: firstEmail }),
                credentials: 'include',
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(data.msg || 'Request failed');
            }

            // Success is deliberately generic — the same message whether or not the
            // email belongs to a registered owner. Pre-fill the login username so the
            // owner can log in straight away once their temp password arrives.
            setFirstMessage(data.msg || 'If you have been invited, an email has been sent to your account with a temporary password.');
            setUsername(firstEmail.trim());
        } catch (err) {
            setFirstError(err.message || 'Request failed');
        } finally {
            setFirstLoading(false);
        }
    };

    const showLogin = () => {
        setMode('login');
        setFirstError('');
    };

    const showFirstTime = () => {
        setMode('first');
        setError('');
        setFirstError('');
        setFirstMessage('');
        setFirstEmail(username || '');
    };

    const showForgot = () => {
        setMode('forgot');
        setError('');
        setForgotError('');
        setForgotMessage('');
        setForgotEmail(username || '');
    };

    const handleForgot = async (e) => {
        e.preventDefault();
        setForgotError('');
        setForgotMessage('');
        setForgotLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: forgotEmail }),
                credentials: 'include',
            });

            const data = await response.json().catch(() => ({}));

            // A malformed email is the only hard client error; everything else
            // comes back 200 with a friendly message, so show that rather than a
            // raw "Request failed".
            if (!response.ok && response.status === 400) {
                setForgotError(data.msg || 'Please enter a valid email address');
                return;
            }
            if (!response.ok && !data.msg) {
                throw new Error('Request failed');
            }

            setForgotMessage(data.msg || 'If this email has an account, a temporary password has been sent.');
            // If they already have an account, pre-fill the login field so they
            // can sign in as soon as the temp password arrives.
            if (data.found && data.username) {
                setUsername(data.username);
            }
        } catch (err) {
            setForgotError(err.message || 'Request failed');
        } finally {
            setForgotLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-surface-50 px-4">
            <div className="card max-w-md w-full">
                <h2 className="page-title text-center">Ruby Racing Login</h2>

                {mode === 'login' ? (
                    <>
                        {error && (
                            <div className="alert-error">{error}</div>
                        )}

                        {successMessage && !error && (
                            <div className="alert-success">{successMessage}</div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-5">
                            <div>
                                <label htmlFor="username" className="label">
                                    Email
                                </label>
                                <input
                                    id="username"
                                    type="text"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    placeholder="Enter your email"
                                    required
                                    className="input w-full"
                                    disabled={loading}
                                />
                            </div>

                            <div>
                                <label htmlFor="password" className="label">
                                    Password
                                </label>
                                <input
                                    id="password"
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    required
                                    className="input w-full"
                                    disabled={loading}
                                />
                            </div>

                            <button
                                type="submit"
                                disabled={loading}
                                className="btn btn-primary w-full"
                            >
                                {loading ? 'Logging in...' : 'Login'}
                            </button>
                        </form>

                        <div className="text-center mt-4 text-sm space-y-2">
                            <div>
                                <button
                                    type="button"
                                    onClick={showForgot}
                                    className="text-ruby-600 hover:underline"
                                >
                                    Forgot your password?
                                </button>
                            </div>
                            <div>
                                <button
                                    type="button"
                                    onClick={showFirstTime}
                                    className="text-ruby-600 hover:underline"
                                >
                                    First time? Get a temporary password
                                </button>
                            </div>
                        </div>
                    </>
                ) : mode === 'first' ? (
                    <>
                        <p className="text-sm text-surface-500 text-center mb-4">
                            Enter the email your account is registered under and we'll send you a
                            temporary password to log in with.
                        </p>

                        {firstError && (
                            <div className="alert-error">{firstError}</div>
                        )}

                        {firstMessage ? (
                            <>
                                <div className="alert-success">{firstMessage}</div>
                                <button
                                    type="button"
                                    onClick={showLogin}
                                    className="btn btn-primary w-full mt-4"
                                >
                                    Back to login
                                </button>
                            </>
                        ) : (
                            <form onSubmit={handleFirstTime} className="space-y-5">
                                <div>
                                    <label htmlFor="firstEmail" className="label">
                                        Email
                                    </label>
                                    <input
                                        id="firstEmail"
                                        type="email"
                                        value={firstEmail}
                                        onChange={(e) => setFirstEmail(e.target.value)}
                                        placeholder="Enter your registered email"
                                        required
                                        className="input w-full"
                                        disabled={firstLoading}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={firstLoading}
                                    className="btn btn-primary w-full"
                                >
                                    {firstLoading ? 'Sending...' : 'Send temporary password'}
                                </button>

                                <div className="text-center text-sm">
                                    <button
                                        type="button"
                                        onClick={showLogin}
                                        className="text-ruby-600 hover:underline"
                                    >
                                        Back to login
                                    </button>
                                </div>
                            </form>
                        )}
                    </>
                ) : (
                    <>
                        <p className="text-sm text-surface-500 text-center mb-4">
                            Already have an account? Enter your email and we'll send a
                            temporary password so you can set a new one.
                        </p>

                        {forgotError && (
                            <div className="alert-error">{forgotError}</div>
                        )}

                        {forgotMessage ? (
                            <>
                                <div className="alert-success">{forgotMessage}</div>
                                <button
                                    type="button"
                                    onClick={showLogin}
                                    className="btn btn-primary w-full mt-4"
                                >
                                    Back to login
                                </button>
                            </>
                        ) : (
                            <form onSubmit={handleForgot} className="space-y-5">
                                <div>
                                    <label htmlFor="forgotEmail" className="label">
                                        Email
                                    </label>
                                    <input
                                        id="forgotEmail"
                                        type="email"
                                        value={forgotEmail}
                                        onChange={(e) => setForgotEmail(e.target.value)}
                                        placeholder="Enter your account email"
                                        required
                                        className="input w-full"
                                        disabled={forgotLoading}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={forgotLoading}
                                    className="btn btn-primary w-full"
                                >
                                    {forgotLoading ? 'Sending...' : 'Reset my password'}
                                </button>

                                <div className="text-center text-sm">
                                    <button
                                        type="button"
                                        onClick={showLogin}
                                        className="text-ruby-600 hover:underline"
                                    >
                                        Back to login
                                    </button>
                                </div>
                            </form>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

export default LoginPage;
